import { PricingTable } from '../components/PricingTable';
import { FAQAccordion } from '../components/FAQAccordion';
import { BookingCTA } from '../components/BookingCTA';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Clock, MapPin, Phone, Sparkles, Heart, Palette, Facebook } from 'lucide-react';
import { SEOHead, createServiceSchema } from '../components/SEOHead';
import { EnhancedSEO } from '../components/EnhancedSEO';
import { FacebookFeed } from '../components/FacebookFeed';
import { ReviewSubmission } from '../components/ReviewSubmission';
import { PublicReviews } from '../components/PublicReviews';
import { generateBreadcrumb } from '../utils/structuredData';
import { fastNailsSchema } from '../utils/businessSchemas';

// Fast Nails hero video
const FAST_NAILS_VIDEO = "https://customer-assets.emergentagent.com/job_eastend-dash/artifacts/kwt74izc_2023-06-27-205802406_1_1.mp4";
const FAST_NAILS_FB = "https://www.facebook.com/share/1KHVbDJYKq/";
const FAST_NAILS_IMAGE = "https://customer-assets.emergentagent.com/job_seo-blog-eastend/artifacts/2thcx71q_Clipped_image_20260208_001528.png";
const FAST_NAILS_PHONE = "(740) 485-8158";
const FAST_NAILS_PHONE_TEL = "tel:+17404858158";

export default function Nails() {
  const services = [
    {
      icon: <Sparkles className="w-6 h-6 text-secondary" />,
      title: "Manicures",
      description: "Basic, gel, French tips, and stunning nail art designs"
    },
    {
      icon: <Heart className="w-6 h-6 text-secondary" />,
      title: "Pedicures",
      description: "Relaxing spa pedicures with massage, exfoliation, and polish"
    },
    {
      icon: <Palette className="w-6 h-6 text-secondary" />,
      title: "Specialty Services",
      description: "Gel extensions, nail repair, custom designs, and more"
    }
  ];

  const pricing = [
    { service: "Basic Manicure", description: "Nail shaping, cuticle care, polish", price: "$25-35" },
    { service: "Gel Manicure", description: "Long-lasting gel polish, 2-3 weeks", price: "$40-45" },
    { service: "Basic Pedicure", description: "Foot soak, exfoliation, massage, polish", price: "$40-60" },
    { service: "Gel Pedicure", description: "Spa pedicure with gel polish", price: "$60-75" },
    { service: "Polish Change (Hands)", description: "Polish removal and reapplication", price: "$15-20" },
    { service: "Polish Change (Feet)", description: "Polish removal and reapplication", price: "$15-20" },
    { service: "French Manicure/Pedicure", description: "Classic French tips", price: "+$5-10" },
    { service: "Nail Art (per nail)", description: "Custom designs and embellishments", price: "$5-15" }
  ];

  const faqs = [
    {
      question: "Do you accept walk-ins or appointments only?",
      answer: "We welcome both walk-ins and appointments! Appointments are by phone only - call us at (740) 485-8158 to ensure availability, especially during peak hours (evenings and weekends)."
    },
    {
      question: "How long does a manicure or pedicure take?",
      answer: "A basic manicure typically takes 30-45 minutes, while a basic pedicure takes 45-60 minutes. Gel services and specialty treatments may take longer. Call (740) 485-8158 and we'll let you know the expected time for your service."
    },
    {
      question: "What brands of polish do you use?",
      answer: "We use professional-grade nail polishes from trusted brands including OPI, Essie, and CND. Our gel polishes are high-quality and long-lasting."
    },
    {
      question: "Do you sanitize tools between clients?",
      answer: "Absolutely! We follow strict sanitation protocols. All metal tools are disinfected in an autoclave between each client, and we use disposable files and buffers for your safety."
    },
    {
      question: "Can I bring my own polish?",
      answer: "Yes, you're welcome to bring your own polish! However, we cannot guarantee the longevity or application quality of outside products."
    },
    {
      question: "Do you offer nail art and custom designs?",
      answer: "Yes! We offer a variety of nail art options from simple accent nails to intricate custom designs. Pricing varies based on complexity. Ask your technician for ideas!"
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept cash, credit cards (Visa, Mastercard, American Express, Discover), and debit cards."
    },
    {
      question: "Do you have a loyalty program or package deals?",
      answer: "Ask us about our special packages and loyalty rewards! We frequently offer discounts for new customers and returning clients."
    }
  ];

  const breadcrumbs = generateBreadcrumb([
    { name: 'Home', path: '/' },
    { name: 'Fast Nails', path: '/nails' }
  ]);

  return (
    <div className="min-h-screen">
      <EnhancedSEO
        title="Fast Nails - Manicures, Pedicures & Nail Art | Eastend Mt Vernon OH"
        description="Professional nail salon at Eastend Mt Vernon featuring expert manicures, pedicures, gel nails, acrylic nails, and custom nail art. Skilled technicians, high-quality polishes, strict sanitation. Walk-ins welcome! 818 Coshocton Ave. Call (740) 485-8158 for appointments."
        keywords="Fast Nails Mt Vernon, nail salon Knox County, manicures Mt Vernon OH, pedicures Mt Vernon, gel nails Knox County, nail art Mt Vernon, acrylic nails Ohio, professional nail care, 818 Coshocton Ave"
        canonicalUrl="https://eastend.website/nails"
        structuredData={[fastNailsSchema, createServiceSchema('Professional Nail Services', 'Expert manicures, pedicures, gel nails, and custom nail art', 25)]}
        breadcrumbs={breadcrumbs}
        ogImage="https://eastend.website/images/nails-salon.jpg"
      />
      {/* Video Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden" data-testid="nails-hero">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="nails-hero-video"
        >
          <source src={FAST_NAILS_VIDEO} type="video/mp4" />
        </video>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />
        
        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 text-center">
          <Badge className="mb-4 bg-pink-500 text-white border-0 px-4 py-1" data-testid="nails-hero-badge">
            Professional Nail Care
          </Badge>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-black leading-tight mb-4 text-white drop-shadow-lg">
            FAST NAILS
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-8 drop-shadow">
            Mount Vernon's premier nail salon. Manicures, pedicures, gel services, and stunning nail art. Pamper yourself with professional care.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <Button 
              size="lg" 
              className="bg-pink-500 hover:bg-pink-600 text-white font-semibold px-8 py-6 text-lg"
              asChild
              data-testid="nails-call-btn"
            >
              <a href={FAST_NAILS_PHONE_TEL}>
                <Phone className="w-5 h-5 mr-2" />
                Call {FAST_NAILS_PHONE}
              </a>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-pink-600 font-semibold px-8 py-6 text-lg bg-white/10 backdrop-blur-sm"
              asChild
              data-testid="nails-facebook-btn"
            >
              <a href={FAST_NAILS_FB} target="_blank" rel="noopener noreferrer">
                <Facebook className="w-5 h-5 mr-2" />
                Follow on Facebook
              </a>
            </Button>
          </div>
          
          {/* Quick Info Bar */}
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-white/90 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Mon-Sat 9AM-7PM</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>{FAST_NAILS_PHONE}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>818 Coshocton Ave, Mt Vernon</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-center">Our Nail Services</h2>
          
          {/* Featured Image + Intro */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 items-center">
            <div className="flex justify-center">
              <img 
                src={FAST_NAILS_IMAGE} 
                alt="Fast Nails - Professional Nail Technician" 
                className="rounded-2xl shadow-xl max-w-sm w-full"
                data-testid="nails-featured-image"
              />
            </div>
            <div className="space-y-4">
              <h3 className="font-serif text-2xl font-bold text-pink-600">Meet Our Expert Team</h3>
              <p className="text-lg text-muted-foreground">
                At Fast Nails, our skilled technicians bring years of experience and a passion for nail artistry. 
                We use only high-quality products and maintain the highest sanitation standards.
              </p>
              <p className="text-lg text-muted-foreground">
                Whether you need a quick polish change or an elaborate nail design, we've got you covered!
              </p>
              <div className="flex items-center gap-2 text-pink-600 font-semibold">
                <Phone className="w-5 h-5" />
                <a href={FAST_NAILS_PHONE_TEL} className="hover:underline">Call for appointments: {FAST_NAILS_PHONE}</a>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {services.map((service, index) => (
              <Card key={index} className="p-6 hover:shadow-md transition-shadow duration-200" data-testid={`service-${index}`}>
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-accent/50">
                    {service.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                    <p className="text-sm text-muted-foreground">{service.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Detailed Services List */}
          <div className="max-w-3xl mx-auto">
            <h3 className="font-serif text-2xl font-bold mb-6">What We Offer</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg bg-card">
                <h4 className="font-semibold mb-2">Manicures</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Basic Manicure</li>
                  <li>• Gel Manicure</li>
                  <li>• French Manicure</li>
                  <li>• Polish Change</li>
                  <li>• Nail Art</li>
                </ul>
              </div>
              <div className="p-4 border rounded-lg bg-card">
                <h4 className="font-semibold mb-2">Pedicures</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Basic Pedicure</li>
                  <li>• Gel Pedicure</li>
                  <li>• Spa Pedicure</li>
                  <li>• Polish Change</li>
                  <li>• Callus Treatment</li>
                </ul>
              </div>
              <div className="p-4 border rounded-lg bg-card">
                <h4 className="font-semibold mb-2">Specialty Services</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Gel Extensions</li>
                  <li>• Nail Repair</li>
                  <li>• Custom Nail Art</li>
                  <li>• Acrylic Removal</li>
                </ul>
              </div>
              <div className="p-4 border rounded-lg bg-card">
                <h4 className="font-semibold mb-2">Add-Ons</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Paraffin Wax Treatment</li>
                  <li>• Hot Stone Massage</li>
                  <li>• Extended Foot Massage</li>
                  <li>• Nail Strengthening</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 lg:py-20 bg-muted">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-center">Pricing</h2>
            <PricingTable 
              items={pricing} 
              note="Prices may vary based on service complexity and technician. Call (740) 485-8158 for exact pricing."
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <div className="max-w-3xl mx-auto">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>

      {/* Call to Book */}
      <section className="py-12 lg:py-20 bg-muted">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <Card className="p-8 sm:p-10 max-w-2xl mx-auto text-center border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-white" data-testid="nails-call-to-book">
            <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-pink-100 flex items-center justify-center">
              <Phone className="w-7 h-7 text-pink-600" />
            </div>
            <h2 className="font-serif text-3xl font-bold mb-3">Call to Book Your Appointment</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Nail appointments and enquiries are handled by phone only. Give us a call and our expert technicians will get you scheduled.
            </p>
            <Button 
              size="lg" 
              className="bg-pink-500 hover:bg-pink-600 text-white font-semibold px-10 py-6 text-xl"
              asChild
              data-testid="nails-call-to-book-btn"
            >
              <a href={FAST_NAILS_PHONE_TEL}>
                <Phone className="w-5 h-5 mr-2" />
                {FAST_NAILS_PHONE}
              </a>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">Walk-ins are always welcome during business hours!</p>
          </Card>
        </div>
      </section>

      {/* Hours & Location */}
      <section className="py-12 lg:py-20 bg-muted">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <Card className="p-8 max-w-2xl mx-auto">
            <h2 className="font-serif text-2xl font-bold mb-6 text-center">Visit Us</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium" data-testid="nails-hours">Monday - Saturday: 9:00 AM - 7:00 PM</p>
                  <p className="text-sm text-muted-foreground mt-1">Walk-ins welcome! Appointments recommended.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">818 Coshocton Ave</p>
                  <p className="text-sm text-muted-foreground">Mt Vernon, OH 43050</p>
                  <p className="text-sm text-muted-foreground italic mt-1">Inside Eastend</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <a href={FAST_NAILS_PHONE_TEL} className="font-medium text-pink-600 hover:underline" data-testid="nails-visit-phone">{FAST_NAILS_PHONE}</a>
                  <p className="text-sm text-muted-foreground">Call for appointments & nail enquiries</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Facebook Feed Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-center">Latest from Fast Nails</h2>
          <div className="flex justify-center">
            <FacebookFeed 
              pageUrl={FAST_NAILS_FB} 
              pageName="Fast Nails"
            />
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-8 text-center">Fast Nails Customer Reviews</h2>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Public Reviews Display */}
            <div>
              <h3 className="font-bold text-xl mb-6">Recent 5-Star Reviews</h3>
              <PublicReviews businessLocation="nails" limit={5} />
            </div>
            
            {/* Review Submission Form */}
            <div>
              <ReviewSubmission defaultLocation="nails" />
            </div>
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 max-w-[1200px]">
          <BookingCTA 
            title="Ready for Beautiful Nails?"
            subtitle="Call (740) 485-8158 to book your appointment, or walk in anytime during our hours!"
            primaryAction={{
              text: "Call (740) 485-8158",
              href: FAST_NAILS_PHONE_TEL
            }}
            directionsUrl="https://www.google.com/maps/dir/?api=1&destination=818+Coshocton+Ave,+Mt+Vernon,+OH+43050"
          />
        </div>
      </section>
    </div>
  );
}